import React, { useState, useRef, useEffect } from 'react'

import { Button } from '../../../common/components/button'
import { Modal } from '../../../common/components/modal'
import { Textarea } from '../../../common/components/textarea'
import { quillService } from '../../../common/services/quill.service'
import { usePageEditorContext } from '../../contexts/page-editor.context'

import { useStyles as useTextBlockStyles } from '../../blocks/text/text-block.style'
import { useStyles } from './text-block-edit-inline.style'

export const TextBlockEditInline = function ({ block }) {
  const textBlockClasses = useTextBlockStyles()
  const classes = useStyles()

  const [isModalOpen, setIsModalOpen] = useState()
  const [formula, setFormula] = useState('')
  const [formulaIndex, setFormulaIndex] = useState()

  const quill = useRef()
  const editorRef = useRef()
  const equationTextareaRef = useRef()
  const toolbarId = `toolbar-${block.key}`

  const pageEditorContext = usePageEditorContext() || {}

  useEffect(() => {
    quill.current = quillService.createQuillInstance(editorRef.current, buildOptions())
    if (block && block.props.value) {
      quill.current.setContents(block.props.value)
    }
    quill.current.on('text-change', onTextChange)
    quill.current.focus()
  }, [])

  useEffect(() => {
    if (isModalOpen) {
      equationTextareaRef.current.focus()
    }
  }, [isModalOpen])

  function buildOptions () {
    return {
      theme: 'snow',
      placeholder: 'Enter your text here ...',
      modules: {
        toolbar: `#${toolbarId}`
      },
      // Limit the formatting that will be allowed when copy/pasting into the editor - https://quilljs.com/docs/formats/
      // Note: 'script' is for superscript/subscript, not script tag
      formats: [ 'header', 'bold', 'italic', 'strike', 'script', 'list', 'link', 'formula' ]
    }
  }

  function onTextChange () {
    if (pageEditorContext.setCurrentEdit) {
      block.props.value = quill.current.getContents()
      pageEditorContext.registerUnsavedChanges()
      pageEditorContext.setCurrentEdit({ block })
    }
  }

  function onFormulaClick () {
    const formulaDelta = getNearestFormulaDelta()

    if (formulaDelta) {
      const formulaValue = getFormulaDeltaValue(formulaDelta.delta)
      setFormula(formulaValue)
      setFormulaIndex(formulaDelta.index)
    }

    setIsModalOpen(true)
  }

  function getNearestFormulaDelta () {
    const selection = quill.current.getSelection(true) // force focus just in case the user clicked outside the editor
    const deltas = [
      { index: selection.index, delta: quill.current.getContents(selection.index, 1) },
      { index: selection.index - 1, delta: quill.current.getContents(selection.index - 1, 1) }
    ]

    return deltas.find(({ delta }) => (!!getFormulaDeltaValue(delta)))
  }

  function getFormulaDeltaValue (delta) {
    return (delta && delta.ops && delta.ops[0] && delta.ops[0].insert && delta.ops[0].insert.formula) || ''
  }

  function handleModalFormulaAdd () {
    if (formulaIndex) {
      quill.current.deleteText(formulaIndex, 1, 'user')
      quill.current.insertEmbed(formulaIndex, 'formula', formula, 'user')
    } else {
      let range = quill.current.getSelection(true)
      if (range != null) {
        let index = range.index + range.length
        quill.current.insertEmbed(range.index, 'formula', formula, 'user')
        quill.current.insertText(index + 1, ' ', 'user')
        quill.current.setSelection(index + 2, 'user')
      }
    }

    closeModal()
  }

  function closeModal () {
    setFormulaIndex()
    setFormula('')
    setIsModalOpen(false)
  }

  return (
    <div className={pageEditorContext.setCurrentEdit ? textBlockClasses.contentInEdit : textBlockClasses.content}>
      <div id={toolbarId} className={textBlockClasses.toolbar}>
        <select className="ql-header" defaultValue={false} data-qa="ql-header">
          <option value={false}></option>
          <option value="2"></option>
          <option value="3"></option>
          <option value="4"></option>
        </select>

        <button className="ql-bold" data-qa="ql-bold"></button>
        <button className="ql-italic" data-qa="ql-italic"></button>
        <button className="ql-strike" data-qa="ql-strike"></button>

        <button className="ql-script" value="sub" data-qa="ql-script-sub"></button>
        <button className="ql-script" value="super" data-qa="ql-script-super"></button>

        <button className="ql-list" value="bullet" data-qa="ql-list-bullet"></button>
        <button className="ql-list" value="ordered" data-qa="ql-list-ordered"></button>

        {/* TODO: Make custom handler for link to allow for picking pages & locations on page */}
        <button className="ql-link" data-qa="ql-link"></button>

        <button className={classes.formulaButton} onClick={onFormulaClick} data-qa="formula-button">
          <div className={classes.formulaIcon}></div>
        </button>
        <button className="ql-clean" data-qa="ql-clean"></button>
      </div>
      <div ref={editorRef} data-qa="page-text-editor" />
      <Modal isOpen={isModalOpen} onClose={() => { closeModal() }} title={formulaIndex === undefined ? 'Insert Equation' : 'Edit Equation'}
        footer={<Button variant="secondary" onClick={handleModalFormulaAdd} data-qa="equation-button">{formulaIndex === undefined ? 'Insert' : 'Update'}</Button>} enableOverlayClick={false}>
        <div className="flex-row flex-center-items">
          <div className={classes.formulaContainer}>
            <Textarea ref={equationTextareaRef} value={formula} onChange={(event) => { setFormula(event.target.value) }} label="Equation" />
          </div>
        </div>
      </Modal>
    </div>
  )
}
